import AvatarComponent from '../../components/avatar/AvatarComponent'
import { Button, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { Add, Check, Clear, Delete, Edit } from '@mui/icons-material'
import { t } from 'i18next'
import React from 'react'
import Candidate from '../../store/types/models/Candidate'
import { useSelector } from 'react-redux'
import { getSidebarState } from '../../store/redux/reducers/sidebarSlice'
import { useEditMode } from '../../store/redux/hooks'

type CandidateDetailsHeaderProps = {
  candidateDetails: Candidate | undefined
  editMode: boolean
  setEditMode: React.Dispatch<React.SetStateAction<boolean>>
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  handleSubmitEdit: () => void
  handleSubmitCreate: () => void
  newCandidate: boolean
  handleCancelButtonClick: () => void
  handleDeleteButtonClick: () => void
}

const CandidateDetailsHeader = ({
  candidateDetails,
  editMode,
  setEditMode,
  setOpen,
  handleSubmitEdit,
  handleSubmitCreate,
  newCandidate,
  handleCancelButtonClick,
  handleDeleteButtonClick,
}: CandidateDetailsHeaderProps) => {
  const theme = useTheme()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'))
  const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'))
  const isDownMd = useMediaQuery(theme.breakpoints.down('md'))
  const isUpLg = useMediaQuery(theme.breakpoints.up('lg'))
  const isSidebarOpen = useSelector(getSidebarState)
  const setIsEditMode = useEditMode().setIsEditMode

  const getAvatarSize = (): number => {
    if (isDownSm || (isSm && isSidebarOpen)) return 150
    if ((isSm && !isSidebarOpen) || isMd) return 75
    return 100
  }

  const handleEditButtonClick = () => {
    setEditMode(true)
    setOpen(true)
    setIsEditMode(true)
  }

  return (
    <Stack direction={isDownSm ? 'column' : 'row'} sx={{ alignItems: isDownSm ? 'center' : 'flex-end', gap: 2 }}>
      <AvatarComponent
        avatar={undefined}
        extension={undefined}
        alt={candidateDetails?.firstName + ' ' + candidateDetails?.lastName}
        size={getAvatarSize()}
        candidate
      />
      <Typography
        component={'h2'}
        variant={isUpLg ? 'h4' : 'h5'}
        fontWeight={700}
        sx={{
          width: '100%',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          color: 'text.highEmphasis',
          display: isDownSm ? 'flex' : 'box',
          justifyContent: isDownSm ? 'center' : 'none',
        }}
      >
        {!newCandidate
          ? `${candidateDetails?.firstName} ${candidateDetails?.lastName}`
          : t('page.candidateDetails.newCandidate')}
        {!editMode && !isDownMd && !isMd && (
          <Typography
            variant="caption"
            color="text.base"
            fontWeight={400}
            sx={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden', pl: '16px' }}
          >
            {candidateDetails?.email ? candidateDetails?.email : ''}
          </Typography>
        )}
      </Typography>

      <Stack
        direction={isDownSm ? 'row' : 'row'}
        sx={{ width: 'fit-content', gap: 2, flexWrap: isDownSm ? 'wrap' : 'nowrap', justifyContent: 'center' }}
      >
        {!editMode && (
          <>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Delete />}
              size="small"
              onClick={handleDeleteButtonClick}
            >
              {t('page.candidateDetails.delete')}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Edit />}
              size="small"
              onClick={handleEditButtonClick}
            >
              {t('page.usersProfile.header.edit')}
            </Button>
          </>
        )}
        {editMode && !newCandidate && (
          <>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Clear />}
              size="small"
              onClick={handleCancelButtonClick}
            >
              {t('page.usersProfile.header.cancel')}
            </Button>
            <Button variant="contained" startIcon={<Check />} size="small" onClick={handleSubmitEdit}>
              {t('page.candidateDetails.save')}
            </Button>
          </>
        )}
        {editMode && newCandidate && (
          <Button variant="contained" startIcon={<Add />} size="small" onClick={handleSubmitCreate}>
            {t('page.candidateDetails.save')}
          </Button>
        )}
      </Stack>
    </Stack>
  )
}

export default CandidateDetailsHeader
